import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useState,
} from 'react'

import { Option } from '@joor/design-system'

import { ValidatedOrders } from './ImportOrders.types'

export type ImportOrdersContextProps = {
  brand: Option | null
  setBrand: Dispatch<SetStateAction<Option | null>>
  brandWarehousesOptions: Option[]
  setBrandWarehousesOptions: Dispatch<SetStateAction<Option[]>>
  buttonText: string
  setButtonText: Dispatch<SetStateAction<string>>
  closeOrderImportModal: boolean
  setCloseOrderImportModal: Dispatch<SetStateAction<boolean>>
  currentStep: number
  setCurrentStep: Dispatch<SetStateAction<number>>
  door: Option | null
  setDoor: Dispatch<SetStateAction<Option | null>>
  doorsOptions: Option[]
  setDoorsOptions: Dispatch<SetStateAction<Option[]>>
  errorMsg: string | undefined
  setErrorMsg: Dispatch<SetStateAction<string | undefined>>
  fileType: string
  setFileType: Dispatch<SetStateAction<string>>
  importEnabled: boolean
  setImportEnabled: Dispatch<SetStateAction<boolean>>
  includeDoor: boolean
  setIncludeDoor: Dispatch<SetStateAction<boolean>>
  includedRetailer: string
  setIncludedRetailer: Dispatch<SetStateAction<string>>
  isLoadingConnectedAccounts: boolean
  setIsLoadingConnectedAccounts: Dispatch<SetStateAction<boolean>>
  isLoadingSecondStepSharedData: boolean
  setIsLoadingSecondStepSharedData: Dispatch<SetStateAction<boolean>>
  isNextDisabled: boolean
  setNextDisabled: Dispatch<SetStateAction<boolean>>
  modalTitle: string
  setModalTitle: Dispatch<SetStateAction<string>>
  oneFileNoDoor: boolean
  setOneFileNoDoor: Dispatch<SetStateAction<boolean>>
  orderType: Option | null
  setOrderType: Dispatch<SetStateAction<Option | null>>
  orderTypeOptions: Option[]
  setOrderTypeOptions: Dispatch<SetStateAction<Option[]>>
  retailer: Option | null
  setRetailer: Dispatch<SetStateAction<Option | null>>
  connectedAccountsOptions: Option[]
  setConnectedAccountsOptions: Dispatch<SetStateAction<Option[]>>
  retailerSearchText: string
  setRetailerSearchText: Dispatch<SetStateAction<string>>
  retailerWarehousesOptions: Option[]
  setRetailerWarehousesOptions: Dispatch<SetStateAction<Option[]>>
  selectedFiles: File[]
  setSelectedFiles: Dispatch<SetStateAction<File[]>>
  showLoader: boolean
  setShowLoader: Dispatch<SetStateAction<boolean>>
  showTitleBar: boolean
  setShowTitleBar: Dispatch<SetStateAction<boolean>>
  showUploadBar: boolean
  setShowUploadBar: Dispatch<SetStateAction<boolean>>
  templateId: string
  setTemplateId: Dispatch<SetStateAction<string>>
  templateType: Option | null
  setTemplateType: Dispatch<SetStateAction<Option | null>>
  validatedOrders?: ValidatedOrders
  setValidatedOrders: Dispatch<SetStateAction<ValidatedOrders | undefined>>
  warehouse: Option | null
  setWarehouse: Dispatch<SetStateAction<Option | null>>
  openedDropdown: string | null
  setOpenedDropdown: Dispatch<SetStateAction<string | null>>
}

const ImportOrdersContext = createContext<ImportOrdersContextProps>(
  {} as ImportOrdersContextProps,
)

type ImportOrdersContextProviderProps = {
  children: ReactNode
  currentStep: number
  setCurrentStep: Dispatch<SetStateAction<number>>
}

const ImportOrdersContextProvider: FC<ImportOrdersContextProviderProps> = ({
  children,
  currentStep,
  setCurrentStep,
}) => {
  const [brand, setBrand] = useState<Option | null>(null)
  const [brandWarehousesOptions, setBrandWarehousesOptions] = useState<
    Option[]
  >([])
  const [buttonText, setButtonText] = useState<string>('Next')
  const [closeOrderImportModal, setCloseOrderImportModal] =
    useState<boolean>(false)
  const [door, setDoor] = useState<Option | null>(null)
  const [doorsOptions, setDoorsOptions] = useState<Option[]>([])
  const [errorMsg, setErrorMsg] = useState<string | undefined>('')
  const [fileType, setFileType] = useState<string>('')
  const [importEnabled, setImportEnabled] = useState<boolean>(true)
  const [includeDoor, setIncludeDoor] = useState<boolean>(false)
  const [includedRetailer, setIncludedRetailer] = useState<string>('')
  const [isLoadingConnectedAccounts, setIsLoadingConnectedAccounts] =
    useState<boolean>(false)
  const [isLoadingSecondStepSharedData, setIsLoadingSecondStepSharedData] =
    useState<boolean>(false)
  const [isNextDisabled, setNextDisabled] = useState<boolean>(true)
  const [modalTitle, setModalTitle] = useState<string>('')
  const [oneFileNoDoor, setOneFileNoDoor] = useState<boolean>(false)
  const [orderType, setOrderType] = useState<Option | null>(null)
  const [orderTypeOptions, setOrderTypeOptions] = useState<Option[]>([])
  const [retailer, setRetailer] = useState<Option | null>(null)
  const [connectedAccountsOptions, setConnectedAccountsOptions] = useState<
    Option[]
  >([])
  const [retailerSearchText, setRetailerSearchText] = useState<string>('')
  const [retailerWarehousesOptions, setRetailerWarehousesOptions] = useState<
    Option[]
  >([])
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [showLoader, setShowLoader] = useState<boolean>(false)
  const [showTitleBar, setShowTitleBar] = useState<boolean>(true)
  const [showUploadBar, setShowUploadBar] = useState<boolean>(false)
  const [templateId, setTemplateId] = useState<string>('')
  const [templateType, setTemplateType] = useState<Option | null>(null)
  const [validatedOrders, setValidatedOrders] = useState<ValidatedOrders>()
  const [warehouse, setWarehouse] = useState<Option | null>(null)
  const [openedDropdown, setOpenedDropdown] = useState<string | null>(null)

  return (
    <ImportOrdersContext.Provider
      value={{
        brand,
        setBrand,
        brandWarehousesOptions,
        setBrandWarehousesOptions,
        buttonText,
        setButtonText,
        closeOrderImportModal,
        setCloseOrderImportModal,
        currentStep,
        setCurrentStep,
        door,
        setDoor,
        doorsOptions,
        setDoorsOptions,
        errorMsg,
        setErrorMsg,
        fileType,
        setFileType,
        importEnabled,
        setImportEnabled,
        includeDoor,
        setIncludeDoor,
        includedRetailer,
        setIncludedRetailer,
        isLoadingConnectedAccounts,
        setIsLoadingConnectedAccounts,
        isLoadingSecondStepSharedData,
        setIsLoadingSecondStepSharedData,
        isNextDisabled,
        setNextDisabled,
        modalTitle,
        setModalTitle,
        oneFileNoDoor,
        setOneFileNoDoor,
        orderType,
        setOrderType,
        orderTypeOptions,
        setOrderTypeOptions,
        retailer,
        setRetailer,
        connectedAccountsOptions,
        setConnectedAccountsOptions,
        retailerSearchText,
        setRetailerSearchText,
        retailerWarehousesOptions,
        setRetailerWarehousesOptions,
        selectedFiles,
        setSelectedFiles,
        showLoader,
        setShowLoader,
        showTitleBar,
        setShowTitleBar,
        showUploadBar,
        setShowUploadBar,
        templateId,
        setTemplateId,
        templateType,
        setTemplateType,
        validatedOrders,
        setValidatedOrders,
        warehouse,
        setWarehouse,
        openedDropdown,
        setOpenedDropdown,
      }}
    >
      {children}
    </ImportOrdersContext.Provider>
  )
}

export { ImportOrdersContext, ImportOrdersContextProvider }
